/**
 * Marketing Strategy — types
 *
 * Sprint 3 · Project Symphony
 *
 * These types mirror the deterministic payload the Planner returns.
 * No AI vocabulary — labels are business-first. "Campaign Capacity"
 * replaces "Budget Ceiling" everywhere the user can see.
 */

export type CampaignStatus =
  | "draft"
  | "planned"
  | "approved"
  | "executing"
  | "paused"
  | "completed"
  | "archived";

export interface CampaignHealth {
  status: "ready" | "ready_with_notes" | "needs_work";
  label: string;
  indicator: string;   // e.g. "🟢"
  readiness_pct: number;
  notes: string[];
}

export interface StrategyExplanationItem {
  title: string;
  detail: string;
}

export interface StrategyBlock {
  executive_summary: string;
  campaign_health: CampaignHealth;
  narrative: string;
  positioning_notes: string;
  guardrails: string[];
  explain_my_strategy: StrategyExplanationItem[];
  capacity_label: string;
  capacity_note: string;
}

export interface AllocationBlock {
  by_content_type: Record<string, number>;
  by_channel: Record<string, number>;
  total_assets_planned: number;
  weeks: number;
}

export interface CadenceBlock {
  posts_per_week: number;
  email_frequency: string;
  blog_frequency: string;
  video_frequency: string;
  landing_page_count: number;
}

export interface TimelinePhase {
  name: string;
  from: string;
  to: string;
  duration_days: number;
  allocation_pct: number;
  focus: string;
}

export interface TimelineBlock {
  start_date: string;
  end_date: string;
  total_days: number;
  phases: TimelinePhase[];
}

export interface ContentStrategyBlock {
  reuse: number;
  refresh: number;
  generate: number;
  total: number;
  by_content_type: Record<
    string,
    { reuse: number; refresh: number; generate: number }
  >;
  vault_ready: boolean;
  note: string;
}

export interface CampaignBlueprint {
  id: string;
  workspace_id: string;
  created_by: string;
  status: CampaignStatus;
  commander_answers: {
    promotion: string;
    business_goal: string;
    target_audience: string;
    duration: string;
    intensity: string;
    channels: string[];
  };
  strategy?: StrategyBlock | null;
  allocation?: AllocationBlock | null;
  cadence?: CadenceBlock | null;
  timeline?: TimelineBlock | null;
  content_strategy?: ContentStrategyBlock | null;
  budget_ceiling_usd?: number | null;
  approved_at?: string | null;
  approved_by?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface CampaignTimelineEvent {
  id: string;
  workspace_id: string;
  campaign_id: string;
  event_name: string;
  display_label: string;
  payload: Record<string, unknown>;
  actor_user_id?: string | null;
  occurred_at: string;
}

export interface BlueprintResponse {
  blueprint: CampaignBlueprint;
}

export interface EventsResponse {
  events: CampaignTimelineEvent[];
}

/** Human labels for content-type keys returned by the planner. */
export const CONTENT_TYPE_LABEL: Record<string, string> = {
  social_instagram_caption: "Instagram posts",
  social_facebook: "Facebook posts",
  social_linkedin: "LinkedIn posts",
  // Patch 37.4.40 · with TikTok and X now correctly typed on the
  // allocation matrix, the founder must see human labels here — a
  // missing entry falls back to the raw slug (`social_tiktok`).
  social_tiktok: "TikTok posts",
  social_x: "X posts",
  social_gbp: "Google Business posts",
  social_hashtag_set: "Hashtag sets",
  email_draft: "Emails",
  blog_draft: "Blog articles",
  landing_page: "Landing pages",
  ad_copy: "Ad creatives",
  sms: "SMS messages",
  image_prompt_social: "Social images",
  image_prompt_banner: "Banner images",
  image_prompt_ad: "Ad images",
  image_prompt_flyer: "Flyer designs",
  flyer_copy: "Flyer copy",
  video_production_kit: "Videos",
};

export const CHANNEL_LABEL: Record<string, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  tiktok: "TikTok",
  x: "X",
  email: "Email",
  blog: "Blog",
  google_business_profile: "Google Business",
  ads_meta: "Meta Ads",
  ads_google: "Google Ads",
  sms: "SMS",
  landing_page: "Landing pages",
};
